/**
 * Uplink mode: collect data fragments from vaults, upload to antenna.
 * Pure functions — no side effects, no rendering.
 */

import type { PlanetState, RuinState, CollectibleNode, GameState, Vec3, ModeConfig } from '../types';
import { ruinJob } from './body';
import { DEFAULT_VAULT_ACTIVATION_TURNS, DEFAULT_FRAGMENTS_REQUIRED } from '../constants';
import { dcos, dsin } from './dmath';

/**
 * Initialize Uplink mode: choose antenna ruin, generate data fragment collectibles.
 */
export function initUplink(
  planets: PlanetState[],
  ruins: RuinState[],
  rng: { int: (min: number, max: number) => number; range: (min: number, max: number) => number },
  config: ModeConfig
): { antennaRuinId: number; collectibles: CollectibleNode[]; updatedRuins: RuinState[] } {
  // Find an antenna ruin to be the upload point
  const antennaRuins = ruins.filter(r => ruinJob(r).uplink);   // X18: the ruin whose job is the uplink
  let antennaRuinId = -1;

  if (antennaRuins.length > 0) {
    const chosen = antennaRuins[rng.int(0, antennaRuins.length - 1)];
    antennaRuinId = chosen.id;
  }

  // Mark non-antenna ruins as data vaults (interactable)
  const activationTurns = config.vaultActivationTurns ?? DEFAULT_VAULT_ACTIVATION_TURNS;
  const updatedRuins = ruins.map(r => {
    if (r.id === antennaRuinId) {
      return { ...r, interactable: true, interactTurns: 1, interactEffect: 'antenna' };
    }
    if (r.ruinType === 'surveyStation' || r.ruinType === 'supplyCache' || r.ruinType === 'refinery') {
      return { ...r, interactable: true, interactTurns: activationTurns, interactEffect: 'dataVault' };
    }
    return r;
  });

  // Generate data fragment collectibles at vault locations
  const collectibles = generateDataFragments(planets, updatedRuins, rng);

  return { antennaRuinId, collectibles, updatedRuins };
}

/**
 * Generate data fragment collectibles near data vault ruins.
 */
function generateDataFragments(
  planets: PlanetState[],
  ruins: RuinState[],
  rng: { range: (min: number, max: number) => number }
): CollectibleNode[] {
  const vaults = ruins.filter(r => r.interactEffect === 'dataVault');
  const fragments: CollectibleNode[] = [];
  let nextId = 0;

  for (const vault of vaults) {
    const theta = rng.range(0, Math.PI * 2);
    const phi = rng.range(0.3, Math.PI - 0.3);
    const surfaceDir: Vec3 = {
      x: dsin(phi) * dcos(theta),
      y: dcos(phi),
      z: dsin(phi) * dsin(theta),
    };

    fragments.push({
      id: nextId++,
      type: 'dataFragment',
      planetIndex: vault.planetIndex,
      surfaceDir,
      value: 1,
      weight: 1,
      collected: false,
      collectedBy: null,
    });
  }

  return fragments;
}

/**
 * Process a fragment upload at the antenna.
 * Returns updated mode state with the fragment's source planet recorded.
 */
export function processFragmentUpload(
  state: GameState,
  playerId: number,
  carryableId: number
): GameState {
  const up = state.modeState.uplink;
  if (!up) return state;

  const carryable = state.carryables.find(c => c.id === carryableId);
  if (!carryable || carryable.carriedBy !== playerId) return state;

  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;

  const teamIndex = player.teamIndex;
  const sourcePlanet = carryable.sourcePlanetIndex ?? carryable.originPlanetIndex;

  const existingFragments = up.uploadedFragments[teamIndex] ?? [];

  // Remove the carryable
  const updatedCarryables = state.carryables.filter(c => c.id !== carryableId);

  return {
    ...state,
    carryables: updatedCarryables,
    modeState: {
      ...state.modeState,
      uplink: {
        ...up,
        uploadedFragments: {
          ...up.uploadedFragments,
          [teamIndex]: [...existingFragments, sourcePlanet],
        },
      },
    },
  };
}
